import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Accordion,
  Table,
  Spinner,
  ProgressBar,
  Toast,
  Alert
} from 'react-bootstrap';
import { FaEdit, FaTrash, FaSyncAlt, FaCheck, FaTimes } from 'react-icons/fa';
import offlineDBService from 'db/dexieDB';
import { ToastContainer } from 'react-toastify';
import { farmerReportApi } from 'api/endpoints/farmers';
import { useNavigate } from 'react-router-dom';
import { getTenantFromToken } from 'utils/routeUtils';
import { FileUploader } from 'api/endpoints/DigitizationUtility';
import { MdEdit } from 'react-icons/md';
import { SYNC_EVENTS, syncEvents } from 'db/syncService';
import { GLOBAL_EVENTS, globalEventBus } from 'utils/eventEmitter';

function OfflineData() {
  const navigate = useNavigate();
  const tenant = getTenantFromToken();
  const [loading, setLoading] = useState(true);
  const [unsyncedData, setUnsyncedData] = useState({
    forms: [],
    farmers: [],
    cultivations: [],
    farms: []
  });
  const [dbStatus, setDbStatus] = useState({});
  const [showDetails, setShowDetails] = useState({});
  const [debugInfo, setDebugInfo] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [syncProgress, setSyncProgress] = useState({
    total: 0,
    current: 0,
    success: 0,
    failed: 0
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [syncErrors, setSyncErrors] = useState([]); // Store sync errors
  const tns_tenant = '03534add672340809e2c70314b1ce20e';
  const [farmerNames, setFarmerNames] = useState({});

  // Add this function to display toast messages
  const displayToast = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  // Get initials from name
  const getInitials = (firstName, lastName) => {
    let initials = '';
    if (firstName) initials += firstName.charAt(0).toUpperCase();
    if (lastName) initials += lastName.charAt(0).toUpperCase();
    if (!initials) initials = '?';
    return initials;
  };

  useEffect(() => {
    fetchUnsyncedData();
    checkAllFarmers();

    // Add event listeners for sync events
    const handleFarmerSynced = event => {
      console.log('Farmer synced event received:', event);
      fetchUnsyncedData();
    };

    const handleFarmSynced = event => {
      console.log('Farm synced event received:', event);
      fetchUnsyncedData();
    };

    const handleCultivationSynced = event => {
      console.log('Cultivation synced event received:', event);
      fetchUnsyncedData();
    };

    const handleSyncCompleted = result => {
      console.log('Sync completed event received:', result);
      fetchUnsyncedData();

      // Display toast based on result
      if (result.success) {
        displayToast(
          `Sync completed successfully. ${result.successCount} items synced.`,
          'success'
        );
      } else {
        displayToast(
          `Sync completed with issues. ${result.failCount} items failed.`,
          'warning'
        );
      }
    };

    const handleSyncFailed = error => {
      console.log('Sync failed event received:', error);
      fetchUnsyncedData();
      displayToast(`Sync failed: ${error.error || 'Unknown error'}`, 'danger');
    };

    const handleDbChanged = () => {
      console.log('Database changed event received');
      fetchUnsyncedData();
    };

    // Register event listeners
    syncEvents.on(SYNC_EVENTS.FARMER_SYNCED, handleFarmerSynced);
    syncEvents.on(SYNC_EVENTS.FARM_SYNCED, handleFarmSynced);
    syncEvents.on(SYNC_EVENTS.CULTIVATION_SYNCED, handleCultivationSynced);
    syncEvents.on(SYNC_EVENTS.COMPLETED, handleSyncCompleted);
    syncEvents.on(SYNC_EVENTS.FAILED, handleSyncFailed);

    // Listen to global event bus for database changes
    globalEventBus.on(GLOBAL_EVENTS.DB_CHANGED, handleDbChanged);
    globalEventBus.on(GLOBAL_EVENTS.SYNC_FAILED, handleSyncFailed);
    globalEventBus.on(GLOBAL_EVENTS.SYNC_COMPLETED, handleSyncCompleted);

    // Cleanup function to remove event listeners
    return () => {
      syncEvents.off(SYNC_EVENTS.FARMER_SYNCED, handleFarmerSynced);
      syncEvents.off(SYNC_EVENTS.FARM_SYNCED, handleFarmSynced);
      syncEvents.off(SYNC_EVENTS.CULTIVATION_SYNCED, handleCultivationSynced);
      syncEvents.off(SYNC_EVENTS.COMPLETED, handleSyncCompleted);
      syncEvents.off(SYNC_EVENTS.FAILED, handleSyncFailed);

      globalEventBus.off(GLOBAL_EVENTS.DB_CHANGED, handleDbChanged);
      globalEventBus.off(GLOBAL_EVENTS.SYNC_FAILED, handleSyncFailed);
      globalEventBus.off(GLOBAL_EVENTS.SYNC_COMPLETED, handleSyncCompleted);
    };
  }, []);

  // Debug function to directly check all farmers
  const checkAllFarmers = async () => {
    try {
      console.log('DEBUG: Directly checking all farmers in database');
      const allFarmers = await offlineDBService.getAllFarmers();
      console.log('DEBUG: All farmers in database:', allFarmers);

      // Log each farmer's synced status
      allFarmers.forEach(farmer => {
        console.log(
          `Farmer ID: ${farmer.id}, Name: ${
            farmer.first_name || 'No Name'
          }, Synced: ${farmer.synced}`
        );
      });

      // Find unsynced farmers
      const unsyncedFarmers = allFarmers.filter(
        farmer => farmer.synced === false
      );
      console.log('DEBUG: Unsynced farmers count:', unsyncedFarmers.length);
      console.log('DEBUG: Unsynced farmers:', unsyncedFarmers);

      setDebugInfo(
        `Total farmers: ${allFarmers.length}, Unsynced: ${unsyncedFarmers.length}`
      );
    } catch (error) {
      console.error('DEBUG: Error checking all farmers:', error);
      setDebugInfo(`Error checking farmers: ${error.message}`);
    }
  };

  const fetchUnsyncedData = async () => {
    setLoading(true);
    try {
      console.log('Fetching unsynced data...');
      const data = await offlineDBService.getUnsyncedData();
      console.log('Unsynced data returned:', data);

      // Additional logging to validate data structure
      console.log('Farmers array length:', data.farmers.length);
      if (data.farmers.length > 0) {
        console.log('Sample farmer:', data.farmers[0]);
      }

      setUnsyncedData(data);
    } catch (error) {
      console.error('Error fetching unsynced data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkDatabaseStatus = async () => {
    try {
      const status = await offlineDBService.checkDatabaseStatus();
      setDbStatus(status);
    } catch (error) {
      console.error('Error checking database status:', error);
    }
  };

  const toggleDetails = (type, id) => {
    setShowDetails(prev => ({
      ...prev,
      [`${type}-${id}`]: !prev[`${type}-${id}`]
    }));
  };

  const formatDate = dateString => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Handle Edit Farmer
  const handleEditFarmer = farmerId => {
    navigate(`/farmer/edit/${farmerId}`);
  };
  // Handle Edit Farmer
  const handleEdit = (key, farmerId) => {
    // console.log(farmerId, 'cultivation.id');
    navigate(`/${key}/edit/${farmerId}`);
  };

  // Add this delete function to your component
  const deleteFarmerRecord = async id => {
    try {
      setDeletingId(id);

      // Delete from database
      await offlineDBService.deleteFarmer(id);

      // Show success message
      displayToast(`Farmer record deleted successfully`, 'success');

      // Refresh data
      await fetchUnsyncedData();
    } catch (error) {
      console.error('Error deleting farmer record:', error);
      displayToast(`Error deleting record: ${error.message}`, 'danger');
    } finally {
      setDeletingId(null);
      setShowDeleteConfirm({});
    }
  };
  // Add this delete function to your component
  const deleteFarmRecord = async id => {
    try {
      setDeletingId(id);

      // Delete from database
      await offlineDBService.deleteFarm(id);

      // Show success message
      displayToast(`Farm record deleted successfully`, 'success');

      // Refresh data
      await fetchUnsyncedData();
    } catch (error) {
      console.error('Error deleting farmer record:', error);
      displayToast(`Error deleting record: ${error.message}`, 'danger');
    } finally {
      setDeletingId(null);
      setShowDeleteConfirm({});
    }
  };
  const deleteCultivationRecord = async id => {
    try {
      setDeletingId(id);

      // Delete from database
      await offlineDBService.deleteCultivation(id);

      // Show success message
      displayToast(`Cultivation record deleted successfully`, 'success');

      // Refresh data
      await fetchUnsyncedData();
    } catch (error) {
      console.error('Error deleting farmer record:', error);
      displayToast(`Error deleting record: ${error.message}`, 'danger');
    } finally {
      setDeletingId(null);
      setShowDeleteConfirm({});
    }
  };

  // Single farmer retry function
  const retryFarmerSync = async farmerId => {
    try {
      setSyncing(true);
      const farmer = unsyncedData.farmers.find(f => f.id === farmerId);

      if (!farmer) {
        displayToast('Farmer not found', 'danger');
        return;
      }

      displayToast(
        `Attempting to sync farmer: ${farmer.first_name || 'Unknown'}`,
        'info'
      );

      try {
        const response = await farmerReportApi.sendFarmerCreate(
          tns_tenant,
          farmer
        );

        // Check if the response has a successful status code (2xx)
        const isSuccessful =
          response &&
          ((response.status >= 200 && response.status < 300) ||
            response.success === true);

        if (isSuccessful) {
          // Force removal of the record
          try {
            await offlineDBService.markAsSyncedAndRemove('farmers', farmer.id);
          } catch (deleteError) {
            console.error(`Error deleting farmer ${farmer.id}:`, deleteError);
            // Try one more approach - direct deletion
            await offlineDBService.deleteFarmer(farmer.id);
          }

          displayToast(
            `Successfully synced farmer: ${farmer.first_name || 'Unknown'}`,
            'success'
          );

          // Remove from errors list
          setSyncErrors(prev => prev.filter(e => e.id !== farmer.id));

          // Update UI immediately
          setUnsyncedData(prev => ({
            ...prev,
            farmers: prev.farmers.filter(f => f.id !== farmer.id)
          }));

          // Refresh data
          await fetchUnsyncedData();
        } else {
          const errorMessage = response?.message || 'Unknown error occurred';
          await offlineDBService.updateFarmerSyncError(farmer.id, errorMessage);
          displayToast(`Failed to sync: ${errorMessage}`, 'danger');
        }
      } catch (error) {
        await offlineDBService.updateFarmerSyncError(
          farmer.id,
          error.message || 'Sync failed'
        );
        displayToast(`Sync error: ${error.message}`, 'danger');
      }
    } catch (error) {
      console.error('Error in retry:', error);
      displayToast(`Retry failed: ${error.message}`, 'danger');
    } finally {
      setSyncing(false);
    }
  };

  const renderToast = () => {
    return (
      <ToastContainer
        position="top-end"
        className="p-3"
        style={{ zIndex: 1070 }}
      >
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={5000}
          autohide
          bg={toastVariant}
        >
          <Toast.Header>
            <strong className="me-auto">
              {toastVariant === 'success'
                ? 'Success'
                : toastVariant === 'danger'
                ? 'Error'
                : 'Notification'}
            </strong>
          </Toast.Header>
          <Toast.Body className={toastVariant === 'dark' ? 'text-white' : ''}>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    );
  };

  // Helper function to display object properties in a table
  const renderObjectProperties = obj => {
    if (!obj) return <div>No data</div>;

    return (
      <Table striped bordered hover size="sm">
        <tbody>
          {Object.entries(obj).map(([key, value]) => {
            // Skip object or array values in this view
            if (typeof value === 'object' && value !== null) {
              value = '[Complex Object]';
            }

            return (
              <tr key={key}>
                <td>
                  <strong>{key}:</strong>
                </td>
                <td>
                  {value !== undefined && value !== null
                    ? String(value)
                    : 'N/A'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  // Improved renderFarmerCard function with profile icon and simplified buttons
  const renderFarmerCard = farmer => {
    const deleteConfirmKey = `delete-${farmer.id}`;
    const hasSyncError = farmer.syncError && farmer.syncAttempted;
    // Get name and profile info
    const firstName = farmer.first_name || '';
    const lastName = farmer.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim() || 'Unknown Farmer';
    const initials = getInitials(firstName, lastName);
    const phoneNumber = farmer.phone_number || farmer.phone || 'No Phone';

    return (
      <Card
        className={`mb-3 ${hasSyncError ? 'border-danger' : ''}`}
        key={farmer.id}
      >
        <Card.Header
          className={`${hasSyncError ? 'bg-danger-subtle' : ''} p-2`}
        >
          {/* First row: Farmer info */}
          <div className="d-flex align-items-start mb-2">
            {/* Profile Icon */}
            <div
              className="profile-icon me-2 bg-primary text-white"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 'bold',
                fontSize: '16px'
              }}
            >
              {initials}
            </div>

            {/* Farmer Info */}
            <div className="d-flex flex-column">
              <div className="fs-mobile fw-medium">{fullName}</div>
              <div className="text-muted small">{phoneNumber}</div>
            </div>

            <Badge
              bg="warning"
              className="ms-2 mt-1 badge-small self-align-start"
            >
              Pending
            </Badge>
            {hasSyncError && (
              <Badge
                bg="danger"
                className="ms-1 mt-1 badge-small self-align-start"
              >
                Failed
              </Badge>
            )}
          </div>

          {/* Second row: Action buttons */}
          <div className="d-flex justify-content-end">
            {showDeleteConfirm[deleteConfirmKey] ? (
              // Confirmation buttons
              <>
                <Button
                  variant="danger"
                  size="sm"
                  className="me-1 btn-icon"
                  disabled={deletingId === farmer.id}
                  onClick={() => deleteFarmerRecord(farmer.id)}
                  title="Confirm Delete"
                >
                  {deletingId === farmer.id ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FaCheck />
                  )}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="btn-icon"
                  onClick={() => {
                    const newState = { ...showDeleteConfirm };
                    newState[deleteConfirmKey] = false;
                    setShowDeleteConfirm(newState);
                  }}
                  title="Cancel"
                >
                  <FaTimes />
                </Button>
              </>
            ) : (
              // Normal action buttons
              <>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="me-1 btn-icon"
                  onClick={() => handleEditFarmer(farmer.id)}
                  title="Edit Farmer"
                >
                  <MdEdit />
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="btn-icon"
                  onClick={() => {
                    const newState = { ...showDeleteConfirm };
                    newState[deleteConfirmKey] = true;
                    setShowDeleteConfirm(newState);
                  }}
                  title="Delete Farmer"
                >
                  <FaTrash />
                </Button>
              </>
            )}
          </div>
        </Card.Header>

        {/* Always show sync error if present */}
        {hasSyncError && (
          <Card.Body className="py-2">
            <Alert variant="danger" className="mb-0 p-2">
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <div className="pe-2">
                  <div className="text-danger fw-medium fs-mobile">
                    {farmer.syncError}
                  </div>
                  <div>
                    <small className="text-muted">
                      Last attempt: {formatDate(farmer.lastSyncAttempt)}
                    </small>
                  </div>
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="btn-icon flex-shrink-0"
                  onClick={() => retryFarmerSync(farmer.id)}
                  disabled={syncing}
                  title="Retry Sync"
                >
                  {syncing ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FaSyncAlt />
                  )}
                </Button>
              </div>
            </Alert>
          </Card.Body>
        )}
      </Card>
    );
  };

  const renderFarmCard = farm => {
    const detailKey = `farms-${farm.id}`;
    const deleteConfirmKey = `delete-${farm.id}`;
    // const farmerName = farmerNames[farm.farmer] || 'Loading...';

    return (
      <Card className="mb-3" key={farm.id}>
        <Card.Header>
          <div className="d-flex justify-content-between flex-wrap">
            <div className="d-flex align-items-start fs-mobile">
              <div>
                <div className="d-flex align-items-center fs-mobile">
                  {' '}
                  <strong>Farm:</strong>
                  <strong className="ms-1">{farm.farm_name}</strong>
                </div>
                <p className="mb-0 text-muted">Area: {farm.area}</p>
              </div>

              <Badge bg="warning" className="ms-2 mt-1 badge-small">
                Pending
              </Badge>
              {/* <p className="mb-0 text-muted">Farmer: {farmerName}</p> */}
            </div>
            <div className="d-flex justify-content-end">
              {showDeleteConfirm[deleteConfirmKey] ? (
                // Confirmation buttons
                <>
                  <Button
                    variant="danger"
                    size="sm"
                    className="me-1 btn-icon"
                    disabled={deletingId === farm.id}
                    onClick={() => deleteFarmRecord(farm.id)}
                    title="Confirm Delete"
                  >
                    {deletingId === farm.id ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FaCheck />
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="btn-icon"
                    onClick={() => {
                      const newState = { ...showDeleteConfirm };
                      newState[deleteConfirmKey] = false;
                      setShowDeleteConfirm(newState);
                    }}
                    title="Cancel"
                  >
                    <FaTimes />
                  </Button>
                </>
              ) : (
                // Normal action buttons
                <>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-1 btn-icon"
                    onClick={() => handleEdit('farm', farm.id)}
                    title="Edit farm"
                  >
                    <MdEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="btn-icon"
                    onClick={() => {
                      const newState = { ...showDeleteConfirm };
                      newState[deleteConfirmKey] = true;
                      setShowDeleteConfirm(newState);
                    }}
                    title="Delete farm"
                  >
                    <FaTrash />
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card.Header>
        {showDetails[detailKey] && (
          <Card.Body>
            {renderObjectProperties(farm)}
            <Card.Text>
              <small className="text-muted">
                This record will be synced when you're back online.
              </small>
            </Card.Text>
          </Card.Body>
        )}
      </Card>
    );
  };
  const renderCultivationCard = cultivation => {
    const detailKey = `farms-${cultivation.id}`;
    const deleteConfirmKey = `delete-${cultivation.id}`;
    // const farmerName = farmerNames[farm.farmer] || 'Loading...';
    const hasSyncError = cultivation.syncError && cultivation.syncAttempted;

    return (
      <Card className="mb-3" key={cultivation.id}>
        <Card.Header>
          <div className="d-flex justify-content-between flex-wrap">
            <div className="d-flex align-items-start fs-mobile">
              <div>
                <div className="d-flex align-items-center fs-mobile">
                  {' '}
                  <strong>Cultivation:</strong>
                  <strong className="ms-1">{cultivation.farm_name}</strong>
                </div>
                <p className="mb-0 text-muted">
                  Area: {cultivation.cultivated_area}
                </p>
              </div>

              <Badge bg="warning" className="ms-2 mt-1 badge-small">
                Pending
              </Badge>
              {/* <p className="mb-0 text-muted">Farmer: {farmerName}</p> */}
            </div>
            <div className="d-flex justify-content-end">
              {showDeleteConfirm[deleteConfirmKey] ? (
                // Confirmation buttons
                <>
                  <Button
                    variant="danger"
                    size="sm"
                    className="me-1 btn-icon"
                    disabled={deletingId === cultivation.id}
                    onClick={() => deleteCultivationRecord(cultivation.id)}
                    title="Confirm Delete"
                  >
                    {deletingId === cultivation.id ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FaCheck />
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="btn-icon"
                    onClick={() => {
                      const newState = { ...showDeleteConfirm };
                      newState[deleteConfirmKey] = false;
                      setShowDeleteConfirm(newState);
                    }}
                    title="Cancel"
                  >
                    <FaTimes />
                  </Button>
                </>
              ) : (
                // Normal action buttons
                <>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-1 btn-icon"
                    onClick={() => handleEdit('cultivation', cultivation.id)}
                    title="Edit cultivation"
                  >
                    <MdEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="btn-icon"
                    onClick={() => {
                      const newState = { ...showDeleteConfirm };
                      newState[deleteConfirmKey] = true;
                      setShowDeleteConfirm(newState);
                    }}
                    title="Delete cultivation"
                  >
                    <FaTrash />
                  </Button>
                </>
              )}
            </div>
          </div>
          {/* Always show sync error if present */}
          {hasSyncError && (
            <Card.Body className="py-2">
              <Alert variant="danger" className="mb-0 p-2">
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                  <div className="pe-2">
                    <div className="text-danger fw-medium fs-mobile">
                      {cultivation.syncError}
                    </div>
                    <div>
                      <small className="text-muted">
                        Last attempt: {formatDate(cultivation.lastSyncAttempt)}
                      </small>
                    </div>
                  </div>
                </div>
              </Alert>
            </Card.Body>
          )}
        </Card.Header>
        {showDetails[detailKey] && (
          <Card.Body>
            {renderObjectProperties(cultivation)}
            <Card.Text>
              <small className="text-muted">
                This record will be synced when you're back online.
              </small>
            </Card.Text>
          </Card.Body>
        )}
      </Card>
    );
  };

  const renderFormCard = form => {
    const detailKey = `forms-${form.id}`;
    return (
      <Card className="mb-3" key={form.id}>
        <Card.Header>
          <div className="d-flex justify-content-between flex-wrap">
            <div className="d-flex align-items-center fs-mobile">
              <strong>Form:</strong>{' '}
              <span className="text-truncate">
                {form.formType || 'Unknown Type'}
              </span>
              <Badge bg="warning" className="ms-2 badge-small">
                Pending
              </Badge>
            </div>
            <Button
              variant="info"
              size="sm"
              className="btn-sm-smaller"
              onClick={() => toggleDetails('forms', form.id)}
            >
              {showDetails[detailKey] ? 'Hide' : 'View'}
            </Button>
          </div>
        </Card.Header>
        {showDetails[detailKey] && (
          <Card.Body>
            <h6>Form Data:</h6>
            <pre
              className="bg-light p-3 rounded"
              style={{ maxHeight: '200px', overflow: 'auto' }}
            >
              {JSON.stringify(form.data, null, 2)}
            </pre>
            <div className="mt-2">
              <strong>Submitted:</strong> {formatDate(form.timestamp)}
            </div>
            <Card.Text>
              <small className="text-muted">
                This form will be submitted when you're back online.
              </small>
            </Card.Text>
          </Card.Body>
        )}
      </Card>
    );
  };

  return (
    <Container className="py-2">
      <Row className="mb-4">
        <Col>
          <h2>Offline Data Report</h2>
          <p className="text-muted">
            This screen shows all data that has been saved locally but not yet
            synchronized with the server.
          </p>
          {/* <Button
            variant="primary"
            onClick={fetchUnsyncedData}
            className="me-2"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              'Refresh Data'
            )}
          </Button> */}
        </Col>
      </Row>

      {/* <Row className="mb-4">
        <Col xs={6}>
          <Card className="text-center h-100">
            <Card.Body>
              <Card.Title>Farmers</Card.Title>
              <h1>{unsyncedData.farmers.length}</h1>
              <Card.Text>Farmers pending</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6}>
          <Card className="text-center h-100">
            <Card.Body>
              <Card.Title>Farms</Card.Title>
              <h1>{unsyncedData.farms.length}</h1>
              <Card.Text>Farms pending</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row> */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading offline data...</p>
        </div>
      ) : (
        <Accordion defaultActiveKey="0" className="mb-4 bg-white">
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              Farmers ({unsyncedData.farmers.length})
            </Accordion.Header>
            <Accordion.Body>
              {unsyncedData.farmers.length === 0 ? (
                <p className="text-center py-3">
                  No farmers pending synchronization.
                </p>
              ) : (
                unsyncedData.farmers.map(farmer => renderFarmerCard(farmer))
              )}
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="1">
            <Accordion.Header>
              Farms ({unsyncedData.farms.length})
            </Accordion.Header>
            <Accordion.Body>
              {unsyncedData.farms.length === 0 ? (
                <p className="text-center py-3">
                  No farms pending synchronization.
                </p>
              ) : (
                unsyncedData.farms.map(farm => renderFarmCard(farm))
              )}
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="2">
            <Accordion.Header>
              Cultivation ({unsyncedData.cultivations.length})
            </Accordion.Header>
            <Accordion.Body>
              {unsyncedData.cultivations.length === 0 ? (
                <p className="text-center py-3">
                  No cultivaiton pending synchronization.
                </p>
              ) : (
                unsyncedData.cultivations.map(cultivation =>
                  renderCultivationCard(cultivation)
                )
              )}
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item eventKey="3">
            <Accordion.Header>
              Form Submissions ({unsyncedData.forms.length})
            </Accordion.Header>
            <Accordion.Body>
              {unsyncedData.forms.length === 0 ? (
                <p className="text-center py-3">
                  No form submissions pending synchronization.
                </p>
              ) : (
                unsyncedData.forms.map(form => renderFormCard(form))
              )}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}

      <Row>
        <Col className="text-center">
          <p className="text-muted">
            <small>
              Note: These records will be automatically synchronized when your
              device reconnects to the internet.
            </small>
          </p>
        </Col>
      </Row>
      {renderToast()}

      {/* Add CSS for mobile responsiveness */}
      {/* <style jsx="true">{`
        @media (max-width: 576px) {
          .fs-mobile {
            font-size: 0.85rem;
          }
          .badge-small {
            font-size: 0.65rem;
          }
          .btn-sm-smaller {
            padding: 0.1rem 0.3rem;
            font-size: 0.7rem;
          }
        }
      `}</style> */}
    </Container>
  );
}

export default OfflineData;
